<template>
  <BasicNavbar />
  <div
    class="w-full h-full flex justify-center mt-[30px] tablet-v:mt-[56px] tablet-h:mt-[87px] px-3 pb-3"
  >
    <div class="w-full max-w-[440px]">
      <!-- Header -->
      <div
        class="font-secondary font-medium text-[26px] tracking-[-0.33px] mb-10"
      >
        <div class="leading-[30px] text-green-700 mb-1.5">
          Protect what's yours
        </div>
        <div class="leading-[30px] text-gray-800">Create your Dacr account</div>
      </div>

      <!-- Form -->
      <div class="flex flex-col mb-[30px]">
        <div class="w-full flex items-start mb-[14px]">
          <div class="w-1/2 mr-2.5">
            <UiInput
              v-model.trim="firstName"
              label="First name"
              :error-msg="firstNameError"
              :disabled="loading"
              :tabindex="1"
            />
          </div>
          <div class="w-1/2">
            <UiInput
              v-model.trim="lastName"
              label="Last name"
              :error-msg="lastNameError"
              :disabled="loading"
              :tabindex="2"
            />
          </div>
        </div>
        <div class="mb-[14px]">
          <UiInput
            v-model="email"
            label="Email"
            type="email"
            :error-msg="emailError"
            :disabled="loading"
            :tabindex="3"
          />
        </div>
        <div class="mb-[14px]">
          <UiInput
            v-model.trim="username"
            label="Username"
            :error-msg="usernameError"
            :disabled="loading"
            :tabindex="4"
          />
        </div>
        <div class="mb-[18px]">
          <UiInput
            v-model="password"
            label="Password"
            type="password"
            :error-msg="passwordError"
            :disabled="loading"
            :tabindex="5"
            @focus="handlePasswordFocus"
          />
        </div>
        <div v-if="showConfirmPassword" class="mb-[18px]">
          <UiInput
            v-model="confirmPassword"
            label="Confirm Password"
            type="password"
            :error-msg="confirmPasswordError"
            :disabled="loading"
            :tabindex="6"
            @submit="handleSignUp"
          />
        </div>
        <div
          class="mb-3 font-secondary text-[12px] leading-[18px] text-gray-800 tracking-[0.3px]"
        >
          By selecting "Agree and sign up" I agree to the Dacr
          <a class="text-link" href="/legal/terms"> Terms </a>
          and
          <a class="text-link -mr-1" href="/legal/privacy"> Privacy Policy </a>
          .
        </div>
        <UiButton
          class="w-full!"
          primary
          :loading="loading"
          @click="handleSignUp"
        >
          Agree and sign up
        </UiButton>
      </div>

      <div class="font-secondary text-[14px] leading-[22px] text-center">
        Have a Dacr account?
        <span class="text-link" :class="linkClasses" @click="handleLoginClick">
          Sign in
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  SearchSort,
  useCreateUserMutation,
  useValidateOtpMutation,
} from '@/shared/graphql/legacy/generated'
import { useExplorerStore } from '@/features/explorer/store'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useUserStore } from '@/shared/stores/user'
import { EMAIL_REGEX, PASSWORD_REGEX } from '@/shared/utils/string'
import { signup } from '@dacr/auth'
import { UiButton, UiInput } from '@dacr/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BasicNavbar from '@/shared/components/BasicNavbar.vue'

const { mutate: validateOTP } = useValidateOtpMutation()
const { mutate: createUser } = useCreateUserMutation()

const route = useRoute()
const currentFolderStore = useExplorerStore()
const userStore = useUserStore()
const router = useRouter()
const toastStore = useToastStore()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const username = ref('')
const password = ref('')
const confirmPassword = ref('')

const showConfirmPassword = ref(false)

const firstNameError = ref('')
const lastNameError = ref('')
const emailError = ref('')
const usernameError = ref('')
const passwordError = ref('')
const confirmPasswordError = ref('')

const loading = ref(false)

onMounted(() => {
  const url = new URL(window.location.href)
  const emailParam = url.searchParams.get('email')

  if (!emailParam) {
    return
  }

  email.value = emailParam
})

watch(firstName, () => {
  firstNameError.value = ''
})

watch(lastName, () => {
  lastNameError.value = ''
})

watch(email, () => {
  emailError.value = ''
})

watch(username, () => {
  usernameError.value = ''
})

watch(password, () => {
  passwordError.value = ''
  confirmPasswordError.value = ''
})

watch(confirmPassword, () => {
  confirmPasswordError.value = ''
})

const linkClasses = computed(() => ({
  ...(loading.value && {
    'pointer-events-none': true,
    'opacity-50': true,
  }),
}))

const handleLoginClick = () => {
  router.push({
    path: '/login',
    query: {
      ott: route.query.ott,
      redirect: route.query.redirect,
    },
  })
}

const handlePasswordFocus = () => {
  showConfirmPassword.value = true
}

const validate = () => {
  let hasError = false

  if (!firstName.value.trim()) {
    firstNameError.value = 'Please enter your first name'
    hasError = true
  }

  if (!/^[A-Za-z\s]+$/.test(firstName.value)) {
    firstNameError.value = 'First name can only contain letters and spaces'
    hasError = true
  }

  if (!lastName.value.trim()) {
    lastNameError.value = 'Please enter your last name'
    hasError = true
  }

  if (!/^[A-Za-z\s]+$/.test(lastName.value)) {
    lastNameError.value = 'Last name can only contain letters and spaces'
    hasError = true
  }

  if (!email.value.trim()) {
    emailError.value = 'Please enter your email address'
    hasError = true
  }

  if (email.value.trim() && !EMAIL_REGEX.test(email.value.trim())) {
    emailError.value = 'Email entered is not valid'
    hasError = true
  }

  if (!username.value.trim()) {
    usernameError.value = 'Please enter a username'
    hasError = true
  }

  if (!/^[A-Za-z0-9._]+$/.test(username.value)) {
    usernameError.value =
      'Username can only contain letters, numbers, periods, and underscores'
    hasError = true
  }

  if (!password.value.trim()) {
    passwordError.value = 'Please enter a password'
    hasError = true
  }

  if (password.value && !PASSWORD_REGEX.test(password.value.trim())) {
    passwordError.value =
      'Password must be 8 characters or more including at least 1 number and 1 special character'
    hasError = true
  }

  if (!confirmPassword.value.trim()) {
    confirmPasswordError.value = 'Please enter a password.'
    hasError = true
  }

  if (
    confirmPassword.value &&
    password.value.trim() !== confirmPassword.value.trim()
  ) {
    confirmPasswordError.value = 'Confirmation password does not match password'
    hasError = true
  }

  return !hasError
}

const handleSignUp = async () => {
  if (!validate()) return

  try {
    loading.value = true

    const referralCode = document.cookie.match(/__aff-id=([a-zA-Z0-9_-]+)/)?.[1]

    await signup({
      email: email.value,
      password: password.value,
      client: 'web',
    })

    const result = await createUser({
      input: {
        email: email.value,
        firstName: firstName.value,
        lastName: lastName.value,
        username: username.value,
        ...(referralCode && {
          referralCode,
        }),
      },
    })

    if (result?.data?.createUser) {
      const user = result.data.createUser

      window.gtag('event', 'signup', {
        user_id: user.id,
        name: user.name,
        username: user.username,
      })

      fbq('track', 'Lead')

      if (import.meta.env.VITE_USE_LINKEDIN) {
        window.lintrk('track', { conversion_id: 21628250 })
      }

      if (route.query.ott) {
        await validateOTP({
          code: route.query.ott as string,
        })

        router.push('/auth/success')

        return
      }

      userStore.setUser(user)
      userStore.isSignedIn = true

      // Check for OAuth redirect
      const redirectUrl = route.query.redirect as string

      if (redirectUrl) {
        window.location.href = redirectUrl
        return
      }

      currentFolderStore.setCurrentFolder({
        __typename: 'FolderByPathResponse',
        id: 'root',
        items: [],
        name: '/',
        next: null,
        numberOfItems: 0,
        parentFolderId: '',
        parentPath: '',
        timestamp: null,
        sortBy: SearchSort.NameAsc,
      })

      router.push('/register/organization')
    }
  } catch (error) {
    if (error instanceof Error) {
      console.error(error)

      if (error.message.toLowerCase().includes('first')) {
        firstNameError.value = error.message
        return
      }

      if (error.message.toLowerCase().includes('last')) {
        lastNameError.value = error.message
        return
      }

      if (error.message.toLowerCase().includes('email')) {
        emailError.value = error.message
        return
      }

      if (error.message.toLowerCase().includes('username')) {
        usernameError.value = error.message
        return
      }

      toastStore.showToast({
        type: ToastType.Error,
        content: error.message,
        error,
      })
    }
  } finally {
    loading.value = false
  }
}
</script>
